import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCompanySummaries, type CompanySummary } from '../../../api/Navigation/moduleConfig';
import {
  getCompanyConfigForEdit,
  getCompanyDemoExposure,
  getCompanyDemoGrants,
  grantDemoSurface,
  patchCompanyConfig,
  revokeDemoSurface,
  type CompanyConfigForEdit,
  type DemoSurfaceGrant,
  type DemoSurfaceRef,
} from '../../../api/Navigation/moduleConfigAdmin';
import {
  buildDelta,
  buildStates,
  diffStates,
  isWebPagePath,
  pageKey,
  rebaseStates,
  type ConfigChange,
  type ConfigStateMap,
} from './configDelta';

/**
 * State and persistence for the Operator Module Configuration page (CO-865 split, CO-860 save).
 *
 * Everything that decides WHAT is saved lives here and in `configDelta.ts` (the pure half). The save
 * is a delta: one PATCH carrying only the changed keys and the version they were staged against. The
 * two replace-all PUTs are no longer called from this screen.
 */

export { pageKey };
export type { ConfigChange, ConfigChangeKind, ConfigStateMap } from './configDelta';

export type ConfigStatus = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * Home lock (CO-861). Module `home` and its page `/` are where every user lands, so they cannot be
 * switched off: the delta save refuses it and the legacy saves coerce it. The ids are the server's
 * (`CompanyConfigDeltaRules.HomeModuleId`, `HomePagePath`); `/pending-actions` stays switchable.
 */
export const HOME_MODULE_ID = 'home';
export const HOME_PAGE_PATH = '/';
export const HOME_PAGE_KEY = pageKey(HOME_MODULE_ID, HOME_PAGE_PATH);

/**
 * The map with `key` forced ON. Applied to the CURRENT maps only, never to a baseline: a tenant that
 * still holds a stored OFF row for Home then shows one staged change (off to on) that the next save
 * corrects, instead of a locked switch drawn ON over a row that says OFF.
 */
function lockOn(states: ConfigStateMap, key: string): ConfigStateMap {
  return states[key] === false ? { ...states, [key]: true } : states;
}

/**
 * Which keys hold a stored row, for the three-state display ("On, set", "Off, set", "On, no row").
 * A web page is stored when ANY row holds its path, under whatever module id (CO-496 rows included),
 * which is the same rule the read and the save use.
 */
function storedKeysOf(config: CompanyConfigForEdit): ReadonlySet<string> {
  const keys = new Set<string>();
  config.modules.forEach(row => keys.add(`module|${row.moduleId}`));
  config.pages.forEach(row => {
    keys.add(isWebPagePath(row.pagePath) ? `path|${row.pagePath}` : `page|${pageKey(row.moduleId, row.pagePath)}`);
  });
  return keys;
}

const NO_STORED_KEYS: ReadonlySet<string> = new Set<string>();

/**
 * Outcome of the last save. Nothing here removes itself on a timer.
 *
 *  - `success` stays until the next edit, save or company switch.
 *  - `error` stays until it is dismissed or the next save starts. `reason` is the server's own
 *    description of a refusal. `outcomeUnknown` means no usable answer came back, so the screen cannot
 *    say whether the write happened and offers a reload instead of claiming either.
 *  - `conflict` (409: the stored configuration changed after this screen read it) stays until a
 *    reload succeeds; `reloadFailed` is set when that reload itself got no answer.
 *
 * The staged changes are kept in every failure case.
 */
export type SaveFeedback =
  | { type: 'success' }
  | { type: 'error'; reason: string | null; outcomeUnknown: boolean }
  | { type: 'conflict'; reloadFailed: boolean }
  | null;

/**
 * The Operator company list, through the shared helper.
 *
 * `failure` is non-null only when the request did not come back clean, so the caller can tell "no
 * companies" from "no answer" (a 500 used to read as "Only Operators can access this page").
 * `failure` is a diagnostic string, never display copy: render a translated message and branch on
 * `status` (401/403 means refused, anything else means retry).
 */
export function useCompanySummaries() {
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const result = await getCompanySummaries();
      if (cancelled) return;
      setCompanies(result.companies);
      setFailure(result.failure);
      setStatus(result.status);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken(n => n + 1), []);

  return { companies, failure, status, loading, reload };
}

export function useModuleConfigEditor(selectedCompanyId: number | null) {
  const [moduleStates, setModuleStates] = useState<ConfigStateMap>({});
  const [pageStates, setPageStates] = useState<ConfigStateMap>({});
  // What the server last returned or accepted: the baseline `changes` and `reset` work against.
  const [baselineModules, setBaselineModules] = useState<ConfigStateMap>({});
  const [baselinePages, setBaselinePages] = useState<ConfigStateMap>({});
  /** Version token of the baseline. A save sends it back; the server answers 409 when it is stale. */
  const [version, setVersion] = useState<string | null>(null);
  /** Keys that hold a stored row in the baseline. See `storedKeysOf`. */
  const [storedKeys, setStoredKeys] = useState<ReadonlySet<string>>(NO_STORED_KEYS);
  const [saving, setSaving] = useState(false);
  /** A reload that keeps the staged changes is in flight (after a conflict or an unanswered save). */
  const [reloading, setReloading] = useState(false);
  const [feedback, setFeedback] = useState<SaveFeedback>(null);

  /**
   * The per-company config load. The company LIST has its own loading flag in `useCompanySummaries`.
   *
   * 🔴 CO-691. Without this the whole switch grid rendered live and ON (`pageStates[key] ?? true`) from
   * the instant a company was picked, while the config read was still in flight. Two things went wrong in that window, and the second one destroys data:
   *   1. A click was silently discarded: `setPageStates(prev => …)` wrote into a map that the arriving
   *      response then REPLACED wholesale, so the switch sprang back with no error.
   *   2. A Save sent that half-built map. Both bulk PUTs are delete-all-then-reinsert, so a payload
   *      holding one key wiped every other page, tab and capability row the tenant had, and reported
   *      "Configuration saved successfully".
   * `ready` is therefore the ONLY state in which this screen is editable or savable. `failed` is its own
   * state rather than an empty map for the same reason: a config fetch that came back `null` used to
   * leave the grid at its all-enabled defaults, one Save away from writing those defaults over the
   * tenant's real configuration. The delta save (CO-860) removed the delete; the gate stays, because a
   * delta computed against defaults that were never read is still a wrong write.
   */
  const [configStatus, setConfigStatus] = useState<ConfigStatus>('idle');
  /**
   * The company whose config the two maps hold. `selectedCompanyId` changes in the render BEFORE the
   * load effect clears the maps, so for that one render the status still says `ready` over the previous
   * company's maps. Comparing the two ids closes that frame: nothing is editable or savable in it.
   */
  const [loadedCompanyId, setLoadedCompanyId] = useState<number | null>(null);
  /** Transport status of a failed config read (null: none, or no failure). 401/403 means refused. */
  const [loadFailureStatus, setLoadFailureStatus] = useState<number | null>(null);
  /** Bumped by the retry affordance to re-run the config effect for the same company. */
  const [configReloadToken, setConfigReloadToken] = useState(0);

  /** The company the screen shows now, readable from a save that started before a switch. */
  const activeCompanyRef = useRef<number | null>(selectedCompanyId);
  /** Synchronous guard shared by save and reload: `saving` in a closure can be one render stale. */
  const busyRef = useRef(false);

  /** The config is loaded and belongs to the selected company: the one state that may be edited. */
  const configReady = configStatus === 'ready' && loadedCompanyId === selectedCompanyId;

  // Populate states when company is selected
  useEffect(() => {
    activeCompanyRef.current = selectedCompanyId;
    // Feedback describes a save of the company that was on screen; it does not follow a switch.
    setFeedback(null);
    setLoadFailureStatus(null);

    if (selectedCompanyId === null) {
      setModuleStates({});
      setPageStates({});
      setBaselineModules({});
      setBaselinePages({});
      setVersion(null);
      setStoredKeys(NO_STORED_KEYS);
      setConfigStatus('idle');
      return;
    }

    // A const, so the narrowing to `number` survives into the async closure below.
    const companyId = selectedCompanyId;

    // 🔴 Cleared BEFORE the fetch, not after it. The previous company's map used to survive the switch
    // until the new response landed, so a Save in that window wrote company A's configuration onto
    // company B.
    setModuleStates({});
    setPageStates({});
    setBaselineModules({});
    setBaselinePages({});
    setVersion(null);
    setStoredKeys(NO_STORED_KEYS);
    setConfigStatus('loading');

    // Guards against an out-of-order resolution: pick company A, pick B before A answers, and A's
    // response must not paint over B's screen (or, worse, become the baseline B is saved against).
    let cancelled = false;

    // Per-tenant config is fetched for the selected company only: the list
    // endpoint no longer carries it (CO-135), so the cost here is O(1) in tenants.
    const loadCompanyConfig = async () => {
      // ONE request for module rows, page rows and their version (CO-864, CO-860). The page rows used
      // to come from a second call that answered `[]` on failure, which this screen then showed as
      // every page, tab and capability ON, complete and savable.
      const result = await getCompanyConfigForEdit(companyId);
      if (cancelled) return;

      if (!result.ok) {
        // It must NOT fall through to the all-enabled defaults: they would read as the tenant's
        // state, and every toggle against them would be saved as a change the tenant never had.
        setLoadFailureStatus(result.status);
        setConfigStatus('failed');
        return;
      }

      const { mStates, pStates } = buildStates(result.config);

      // Home is locked ON in what is shown and staged; the baselines stay as read (see `lockOn`).
      setModuleStates(lockOn(mStates, HOME_MODULE_ID));
      setPageStates(lockOn(pStates, HOME_PAGE_KEY));
      setBaselineModules(mStates);
      setBaselinePages(pStates);
      setVersion(result.config.version);
      setStoredKeys(storedKeysOf(result.config));
      setLoadedCompanyId(companyId);
      setConfigStatus('ready');
    };

    loadCompanyConfig();

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, configReloadToken]);

  // The list the review step shows and the delta save sends. Empty unless `configReady`, so a
  // half-loaded or failed screen can never present itself as having unsaved work.
  const changes = useMemo<ConfigChange[]>(
    () =>
      configReady
        ? [
            ...diffStates(baselineModules, moduleStates, 'module'),
            ...diffStates(baselinePages, pageStates, 'page'),
          ]
        : [],
    [configReady, baselineModules, baselinePages, moduleStates, pageStates]
  );

  // The change bar is mounted on `isDirty` alone, so this is also what keeps Save unreachable until
  // the config read has landed.
  const isDirty = changes.length > 0;

  /** A saved-successfully message describes the previous state; the next edit makes it stale. */
  const clearSuccess = useCallback(
    () => setFeedback(prev => (prev?.type === 'success' ? null : prev)),
    []
  );

  const toggleModule = useCallback((moduleId: string, value: boolean) => {
    // The page draws Home locked; this is the backstop for any other way in.
    if (moduleId === HOME_MODULE_ID && !value) return;
    setModuleStates(prev => ({ ...prev, [moduleId]: value }));
    clearSuccess();
  }, [clearSuccess]);

  const togglePage = useCallback((moduleId: string, pagePath: string, value: boolean) => {
    const key = pageKey(moduleId, pagePath);
    if (key === HOME_PAGE_KEY && !value) return;
    setPageStates(prev => ({ ...prev, [key]: value }));
    clearSuccess();
  }, [clearSuccess]);

  const isModuleEnabled = useCallback(
    (moduleId: string) => moduleStates[moduleId] ?? true,
    [moduleStates]
  );

  const isPageEnabled = useCallback(
    (moduleId: string, pagePath: string) => pageStates[pageKey(moduleId, pagePath)] ?? true,
    [pageStates]
  );

  /** What the server holds for a module, whatever is staged. Decides what the tenant sees NOW. */
  const isModuleEnabledSaved = useCallback(
    (moduleId: string) => baselineModules[moduleId] ?? true,
    [baselineModules]
  );

  /** True when the baseline holds a stored row for the key; false means the value is only the default. */
  const hasStoredRow = useCallback(
    (map: 'module' | 'page', key: string) => {
      if (map === 'module') return storedKeys.has(`module|${key}`);
      const at = key.indexOf(':');
      const pagePath = at < 0 ? '' : key.slice(at + 1);
      return isWebPagePath(pagePath) ? storedKeys.has(`path|${pagePath}`) : storedKeys.has(`page|${key}`);
    },
    [storedKeys]
  );

  // Discard returns to the baseline, except that a stored OFF row for Home stays staged for correction.
  const reset = useCallback(() => {
    setModuleStates(lockOn(baselineModules, HOME_MODULE_ID));
    setPageStates(lockOn(baselinePages, HOME_PAGE_KEY));
    clearSuccess();
  }, [baselineModules, baselinePages, clearSuccess]);

  /**
   * Take `config` as the new baseline and keep the Operator's own edits on top of it.
   *
   * The current maps are rebuilt from the server's answer, never left as this screen's idea of what
   * was saved: the server applies a web page's value to every row holding that path, and another
   * writer may have changed other keys. See `rebaseStates` for what counts as an edit.
   */
  const adoptConfig = useCallback(
    (config: CompanyConfigForEdit, referenceModules: ConfigStateMap, referencePages: ConfigStateMap) => {
      const fresh = buildStates(config);
      setBaselineModules(fresh.mStates);
      setBaselinePages(fresh.pStates);
      setModuleStates(current => lockOn(rebaseStates(fresh.mStates, referenceModules, current), HOME_MODULE_ID));
      setPageStates(current => lockOn(rebaseStates(fresh.pStates, referencePages, current), HOME_PAGE_KEY));
      setVersion(config.version);
      setStoredKeys(storedKeysOf(config));
    },
    []
  );

  const save = useCallback(async () => {
    // `configReady` is re-checked here and not left to the disabled button: the bar is not the only way
    // in (Enter on a focused control, a stale click queued before a company switch), and a delta is
    // only meaningful against a baseline that was actually read.
    if (selectedCompanyId === null || !isDirty || !configReady || version === null || busyRef.current) return;

    const companyId = selectedCompanyId;
    const sentModules = moduleStates;
    const sentPages = pageStates;
    // Only the keys that differ from the baseline. A key the Operator did not touch is not in the
    // request, so the server cannot overwrite or delete it on this screen's behalf.
    const delta = buildDelta(changes, version);

    busyRef.current = true;
    setSaving(true);
    setFeedback(null);

    const result = await patchCompanyConfig(companyId, delta);

    busyRef.current = false;
    setSaving(false);

    // The page disables its company picker while a save is in flight, so this is a backstop: a save
    // that resolves after the screen moved to another company must not become that company's baseline
    // or report on it.
    if (activeCompanyRef.current !== companyId) return;

    if (result.ok) {
      // No company-list refetch needed: the list is id + name only and the save changed neither. A
      // toggle made while the request was in flight differs from what was sent, so it stays staged.
      adoptConfig(result.config, sentModules, sentPages);
      setFeedback({ type: 'success' });
      return;
    }
    if (result.outcome === 'conflict') {
      setFeedback({ type: 'conflict', reloadFailed: false });
      return;
    }
    // Rejected: one transaction, nothing written, the baseline is still true and Discard is honest.
    // Unknown: the baseline may be stale; the message offers the reload below instead of guessing.
    setFeedback({ type: 'error', reason: result.reason, outcomeUnknown: result.outcome === 'unknown' });
  }, [selectedCompanyId, isDirty, configReady, version, moduleStates, pageStates, changes, adoptConfig]);

  /**
   * Read the company's configuration again and keep the staged changes on top of it.
   *
   * The way out of a conflict, and of a save that got no answer. A staged change the server already
   * holds stops being a change; the rest stay staged against the new version, ready to be reviewed
   * and applied again. Unlike `retryLoad` this never clears the maps, so nothing is lost if the
   * read fails.
   */
  const reloadKeepingChanges = useCallback(async () => {
    if (selectedCompanyId === null || !configReady || busyRef.current) return;

    const companyId = selectedCompanyId;
    const oldBaselineModules = baselineModules;
    const oldBaselinePages = baselinePages;

    busyRef.current = true;
    setReloading(true);

    const result = await getCompanyConfigForEdit(companyId);

    busyRef.current = false;
    setReloading(false);

    if (activeCompanyRef.current !== companyId) return;

    if (!result.ok) {
      // The staged changes and the old baseline are untouched. Say that the reload failed, in the
      // place the Operator pressed it from.
      setFeedback(previous =>
        previous?.type === 'conflict'
          ? { type: 'conflict', reloadFailed: true }
          : { type: 'error', reason: null, outcomeUnknown: true }
      );
      return;
    }

    adoptConfig(result.config, oldBaselineModules, oldBaselinePages);
    setFeedback(null);
  }, [selectedCompanyId, configReady, baselineModules, baselinePages, adoptConfig]);

  const dismissFeedback = useCallback(() => setFeedback(null), []);
  const retryLoad = useCallback(() => setConfigReloadToken(n => n + 1), []);

  return {
    configStatus,
    configReady,
    loadFailureStatus,
    moduleStates,
    pageStates,
    isModuleEnabled,
    isModuleEnabledSaved,
    isPageEnabled,
    hasStoredRow,
    toggleModule,
    togglePage,
    isDirty,
    changes,
    reset,
    save,
    saving,
    reloadKeepingChanges,
    reloading,
    feedback,
    dismissFeedback,
    retryLoad,
  };
}

/* ── Demo surfaces of the selected company (CO-849 grants, CO-861). ── */

export type DemoAccessStatus = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * What kind of company is open, as the server says it (`demo-exposure/company/{id}`).
 *
 * Only `live` and `unknown` ask for a confirmation before a demo surface is granted. `unknown` means
 * the read did not answer: the screen must not treat "could not tell" as "not live".
 */
export type DemoTenantKind = 'live' | 'demo' | 'test' | 'unknown';

/** One surface is a page path, a tab or a module; this is its identity on this screen. */
export function demoSurfaceId(surface: DemoSurfaceRef): string {
  const scope = surface.surfaceKind === 'tab' ? surface.moduleId ?? '' : '';
  return `${surface.surfaceKind}|${scope}|${surface.surfaceKey}`.toLowerCase();
}

/**
 * The demo list of one company with its grants, and the two writes on it.
 *
 * Since CO-849 a demo surface is shown to a company's users by an active GRANT alone; the stored
 * switch for that key is inert in either position. So this is a separate, immediate control and not
 * part of the staged delta: a grant or a revoke is one request, applied at once, answered with the
 * list as it now stands. The config `version` is computed from stored rows only and is not affected.
 */
export function useDemoSurfaceAccess(selectedCompanyId: number | null) {
  const [status, setStatus] = useState<DemoAccessStatus>('idle');
  const [surfaces, setSurfaces] = useState<DemoSurfaceGrant[]>([]);
  const [tenantKind, setTenantKind] = useState<DemoTenantKind>('unknown');
  /** The company the list belongs to; closes the one render between a switch and the effect. */
  const [loadedCompanyId, setLoadedCompanyId] = useState<number | null>(null);
  const [loadFailureStatus, setLoadFailureStatus] = useState<number | null>(null);
  /** `demoSurfaceId` of the surface whose grant or revoke is in flight. One at a time. */
  const [pendingId, setPendingId] = useState<string | null>(null);
  /** The last write that failed. Stays until dismissed or the next write starts. Nothing was written. */
  const [actionError, setActionError] = useState<{ id: string; reason: string | null } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const activeCompanyRef = useRef<number | null>(selectedCompanyId);
  const busyRef = useRef(false);

  useEffect(() => {
    activeCompanyRef.current = selectedCompanyId;
    // Cleared before the read, for the same reason the editor clears its maps: company A's grants
    // must never be drawn, or acted on, under company B's name.
    setSurfaces([]);
    setTenantKind('unknown');
    setActionError(null);
    setPendingId(null);
    setLoadFailureStatus(null);
    busyRef.current = false;

    if (selectedCompanyId === null) {
      setStatus('idle');
      return undefined;
    }

    const companyId = selectedCompanyId;
    let cancelled = false;
    setStatus('loading');

    (async () => {
      const [grants, exposure] = await Promise.all([
        getCompanyDemoGrants(companyId),
        getCompanyDemoExposure(companyId),
      ]);
      if (cancelled) return;

      if (!grants.ok) {
        setLoadFailureStatus(grants.status);
        setStatus('failed');
        return;
      }

      // A failed exposure read leaves the kind `unknown`, which asks for the confirmation.
      if (exposure.ok) {
        const company = exposure.value;
        setTenantKind(company.isLive ? 'live' : company.isDemoTenant ? 'demo' : company.isTestCompany ? 'test' : 'unknown');
      }
      setSurfaces(grants.value.surfaces);
      setLoadedCompanyId(companyId);
      setStatus('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, reloadToken]);

  const ready = status === 'ready' && loadedCompanyId === selectedCompanyId;

  const write = useCallback(
    async (surface: DemoSurfaceRef, grant: boolean) => {
      if (selectedCompanyId === null || !ready || busyRef.current) return;

      const companyId = selectedCompanyId;
      const id = demoSurfaceId(surface);
      busyRef.current = true;
      setPendingId(id);
      setActionError(null);

      const result = grant ? await grantDemoSurface(companyId, surface) : await revokeDemoSurface(companyId, surface);

      // A write that resolves after the screen moved on must not paint the other company's list.
      // The effect of the switch has already reset the busy flag and the pending id.
      if (activeCompanyRef.current !== companyId) return;
      busyRef.current = false;
      setPendingId(null);

      if (result.ok) setSurfaces(result.value.surfaces);
      else setActionError({ id, reason: result.reason });
    },
    [selectedCompanyId, ready]
  );

  const grant = useCallback((surface: DemoSurfaceRef) => write(surface, true), [write]);
  const revoke = useCallback((surface: DemoSurfaceRef) => write(surface, false), [write]);
  const dismissActionError = useCallback(() => setActionError(null), []);
  const retryLoad = useCallback(() => setReloadToken(n => n + 1), []);

  return {
    /** `loading` also covers the render in which the list still belongs to the previous company. */
    status: status === 'ready' && !ready ? ('loading' as DemoAccessStatus) : status,
    surfaces: ready ? surfaces : [],
    tenantKind,
    /** True when granting must be confirmed first: a live company, or one the server could not classify. */
    confirmGrant: tenantKind === 'live' || tenantKind === 'unknown',
    loadFailureStatus,
    pendingId,
    actionError,
    grant,
    revoke,
    dismissActionError,
    retryLoad,
  };
}
